// frontend/components/Sidebar.tsx

"use client";

import { useState, useEffect } from "react";
import { useSession, signOut } from "next-auth/react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { Users, FileText, AlertTriangle, RefreshCw, Clock, MoreHorizontal, Star, Library, Trash2, LogOut, Search, ArrowLeftRight, ChevronsLeft } from "lucide-react";
import { useTheme } from "next-themes";

import SidebarSection from "./sidebar/SidebarSection";
import SidebarTreeItem from "./sidebar/SidebarTreeItem";
import CreatePageModal from "./sidebar/CreatePageModal";
import ContextMenu from "./sidebar/ContextMenu";
import TrashModal from "./sidebar/TrashModal";
import SearchModal from "./sidebar/SearchModal";
import NotificationInbox from "./notifications/NotificationInbox";
import SettingsModal from "./settings/SettingsModal";
import RenameModal from "./sidebar/RenameModal";
import DeleteModal from "./sidebar/DeleteModal";
import SidebarProfile from "./sidebar/SidebarProfile";
import ToastContainer from "./ui/ToastContainer";
import MoveDocModal from "./sidebar/MoveDocModal";
import DocPermissionModal from "./sidebar/DocPermissionModal";
import TransferConfirmModal, { TransferPayload } from "./sidebar/TransferConfirmModal";

import { SidebarContext } from "../contexts/SidebarContext";
import { useSidebarDragDrop } from "../hooks/useSidebarDragDrop";
import { useSidebarLogic } from "../hooks/useSidebarLogic";
import { API_URL, revokeServerSession } from "../utils/api";
import { UserSession } from "../utils/documentUtils";

/** 사이드바 테마별 배경 그라디언트 클래스를 반환합니다. */
function getThemeBg(colorTheme: string): string {
  switch (colorTheme) {
    // 라이트 모드 그라데이션은 Tailwind 600~800의 높은 채도가 "쨍하다"는 피드백으로 탈채도·소폭
    // 밝게 조정한 커스텀 색으로 교체(정체성 유지). 다크 스톱은 이미 깊고 차분해 그대로 둔다.
    case "red":      return "bg-gradient-to-b from-[#c96a63] to-[#a44e48] dark:from-red-900/90 dark:to-neutral-950";
    case "green":    return "bg-gradient-to-b from-[#4f9d83] to-[#3a7862] dark:from-emerald-900/90 dark:to-zinc-950";
    case "purple":   return "bg-gradient-to-b from-[#8b74c0] to-[#6a5199] dark:from-violet-900/90 dark:to-slate-950";
    case "orange":   return "bg-gradient-to-b from-[#dd9155] to-[#bb6f38] dark:from-orange-900/90 dark:to-stone-950";
    case "lavender": return "bg-gradient-to-b from-violet-400 to-violet-500 dark:from-violet-950 dark:to-slate-950";
    case "rose":     return "bg-gradient-to-b from-rose-400 to-rose-500 dark:from-rose-950 dark:to-slate-950";
    case "mint":     return "bg-gradient-to-b from-teal-400 to-teal-500 dark:from-teal-950 dark:to-slate-950";
    case "amber":    return "bg-gradient-to-b from-amber-400 to-amber-500 dark:from-amber-950 dark:to-stone-950";
    case "neutral":  return "bg-gray-50 dark:bg-zinc-950";
    default:         return "bg-gradient-to-b from-[#006DBA] to-[#004070] dark:from-slate-800 dark:to-slate-950"; // blue
  }
}

/**
 * 사이드바 최상위 컴포넌트.
 * SidebarContext를 제공하고, 모든 모달·메뉴의 렌더링 책임을 집니다.
 */
export default function Sidebar() {
  const { data: session } = useSession();
  const user = session?.user as UserSession | undefined;
  const router = useRouter();
  const pathname = usePathname();
  const { theme, resolvedTheme, setTheme } = useTheme();

  const {
    documents, isError, isSettingsOpen, setIsSettingsOpen, isTrashOpen, setIsTrashOpen,
    colorTheme, setColorTheme, expandedDocs, setExpandedDocs, contextMenu, setContextMenu,
    renameModal, setRenameModal, deleteModal, setDeleteModal, createModal, setCreateModal,
    fetchDocuments, openCreateModal, submitCreateDocument, handleContextMenu, handleMenuRename,
    submitRename, handleMenuDelete, submitDelete, handleMenuDuplicate, handleToggleFavorite,
    handleRemoveFromRecent, handleEndShare, handleCopyLink, handleMoveDocument,
    teamRootDocs, personalRootDocs, handoverRootDocs, recentDocs,
  } = useSidebarLogic({ user, router, pathname, setTheme });

  // 현재 선택된 색상 테마는 유지하고, 모드만 라이트/다크로 전환한다.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.shiftKey || e.key.toLowerCase() !== "l" || e.isComposing) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select")) return;

      e.preventDefault();
      const currentMode = theme === "system" ? resolvedTheme : theme;
      const nextMode = currentMode === "dark" ? "light" : "dark";
      setTheme(nextMode);
      if (user?.id) localStorage.setItem(`alpha-theme-mode-${user.id}`, nextMode);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [resolvedTheme, setTheme, theme, user?.id]);

  // ── 검색 모달 상태 + Ctrl+K 단축키 ──
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "k") { e.preventDefault(); setIsSearchOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── 옮기기 모달 상태 ──
  const [moveModal, setMoveModal] = useState({
    visible: false,
    docId: null as string | null,
    sourceWorkspaceType: "PERSONAL" as string,
    x: 0,
    y: 0,
  });

  const handleOpenMove = () => {
    if (!contextMenu.docId) return;
    const sourceDoc = documents.find((d: any) => d.id === contextMenu.docId);
    setContextMenu((prev) => ({ ...prev, visible: false }));
    setMoveModal({
      visible: true,
      docId: contextMenu.docId,
      sourceWorkspaceType: sourceDoc?.workspaceType || "PERSONAL",
      x: contextMenu.x,
      y: contextMenu.y,
    });
  };

  const handleConfirmMove = (targetParentId: string | null, targetWorkspaceType: string) => {
    if (!moveModal.docId) return;
    setMoveModal((prev) => ({ ...prev, visible: false }));
    handleMoveDocument(moveModal.docId, targetParentId, targetWorkspaceType);
  };

  // ── 권한 설정 모달 상태 ──
  const [permissionModal, setPermissionModal] = useState<{
    visible: boolean;
    docId: string;
    docTitle: string;
    isGuestAllowed: boolean;
    creatorName?: string | null;
  } | null>(null);

  const handleOpenPermissionModal = () => {
    if (!contextMenu.docId) return;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    const doc = documents.find((d: any) => d.id === contextMenu.docId);
    setPermissionModal({
      visible: true,
      docId: contextMenu.docId,
      docTitle: contextMenu.title || "",
      isGuestAllowed: doc?.isGuestAllowed ?? false,
      creatorName: doc?.creatorName ?? null,
    });
  };

  // ── 새 탭·사이드 뷰 핸들러 ──
  const handleOpenNewTab = () => {
    if (!contextMenu.docId) return;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    window.open(`/doc/${contextMenu.docId}`, "_blank");
  };

  const handleOpenSideView = () => {
    if (!contextMenu.docId) return;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    window.dispatchEvent(new CustomEvent("open-side-view", { detail: { docId: contextMenu.docId } }));
  };

  // ── 드래그앤드롭 (워크스페이스 간 이관 포함) ──
  const {
    dropTarget, dragOverId, handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleReorder,
    transferModal, setTransferModal, executeTransfer,
  } = useSidebarDragDrop({ documents, user, apiUrl: API_URL, fetchDocuments, setExpandedDocs });

  const isNeutral = colorTheme === "neutral";
  const favoriteDocs = documents.filter((d: any) => d.isFavorite === true);

  return (
    <SidebarContext.Provider value={{
      docs: documents, user, expandedDocs, setExpandedDocs, dropTarget,
      handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleReorder, handleContextMenu,
      openCreateModal, colorTheme,
    }}>
      <div
        className={`w-72.5 h-full flex flex-col transition-colors duration-300 dark:border-r dark:border-zinc-800/50 ${getThemeBg(colorTheme)} ${isNeutral ? "text-gray-800 dark:text-gray-200" : "text-white"}`}
      >
        {/* 로고 헤더 */}
        <Link
          href="/"
          className={`p-4 flex items-center space-x-3 font-bold text-xl border-b transition-colors ${
            isNeutral
              ? "border-gray-200 dark:border-zinc-800 hover:bg-gray-200/50 dark:hover:bg-white/5"
              : "border-white/10 hover:bg-white/5"
          }`}
        >
          <img
            src="/Logo.png"
            alt="Logo"
            className={`h-6 w-auto object-contain ${
              isNeutral ? "dark:filter dark:brightness-0 dark:invert" : "filter brightness-0 invert"
            }`}
          />
          <span className="tracking-wide">AlphaNote</span>
        </Link>

        {/* 메인 스크롤 영역 */}
        <div className="sidebar-scroll flex-1 overflow-y-auto px-2 py-3 space-y-4">
          <div className="flex items-stretch gap-1">
            <div className="flex-1 min-w-0">
              <SidebarProfile user={user} isNeutral={isNeutral} onClick={() => setIsSettingsOpen(true)} />
            </div>
            <button
              onClick={() => { void revokeServerSession(user?.accessToken).finally(() => signOut({ callbackUrl: "/login" })); }}
              title="로그아웃"
              className="shrink-0 w-12 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>

          {/* 검색 버튼 + 사이드바 접기 버튼 */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsSearchOpen(true)}
              className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12px] transition-colors border ${
                isNeutral
                  ? "border-gray-200 dark:border-zinc-700 text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 bg-white/60 dark:bg-zinc-900/40"
                  : "border-white/15 text-white/60 hover:bg-white/10 bg-white/5"
              }`}
            >
              <Search size={13} className="shrink-0" />
              <span className="flex-1 text-left truncate">검색</span>
              <span className={`text-[10px] shrink-0 ${isNeutral ? "text-gray-400 dark:text-zinc-600" : "text-white/30"}`}>Ctrl+K</span>
            </button>
            {/* 알림함 — 지나간 알림을 다시 볼 수 있게 한다 */}
            <NotificationInbox accessToken={user?.accessToken} />
            {/* << 사이드바 숨기기 */}
            <button
              onClick={() => window.dispatchEvent(new Event("sidebar-toggle"))}
              title="사이드바 숨기기"
              className={`shrink-0 p-1.5 rounded-lg transition-colors border ${
                isNeutral
                  ? "border-gray-200 dark:border-zinc-700 text-gray-400 dark:text-zinc-500 hover:bg-gray-100 dark:hover:bg-zinc-800 bg-white/60 dark:bg-zinc-900/40"
                  : "border-white/15 text-white/40 hover:bg-white/10 bg-white/5"
              }`}
            >
              <ChevronsLeft size={14} />
            </button>
          </div>

          {isError ? (
            /* 백엔드 연결 실패 시 에러 배너 */
            <div className="mx-1 my-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-center select-none animate-fadeIn">
              <AlertTriangle className="mx-auto text-red-400 mb-2 shrink-0" size={22} />
              <p className="text-xs font-bold text-red-200 mb-1">서버 연결 실패</p>
              <p className="text-[10px] text-white/50 leading-relaxed mb-3">
                새로고침 후에도 지속되면<br />관리자에게 연락해 주세요.
              </p>
              <button
                onClick={fetchDocuments}
                className="w-full py-1.5 bg-white/10 hover:bg-white/20 text-white rounded text-[11px] font-bold transition-all border border-white/10 flex items-center justify-center space-x-1"
              >
                <RefreshCw size={11} />
                <span>다시 시도</span>
              </button>
            </div>
          ) : (
            <>
              {/* 최근 문서 섹션 */}
              <SidebarSection
                title="최근 문서"
                icon={<Clock size={14} />}
                isEmpty={recentDocs.length === 0}
                emptyMessage="최근 문서가 없습니다."
              >
                {recentDocs.map((doc: any) => (
                  <SidebarTreeItem key={`recent-${doc.id}`} doc={doc} level={0} section="recent" />
                ))}
                {recentDocs.length >= 5 && (
                  <Link
                    href="/library"
                    className={`group flex items-center text-[12px] rounded cursor-pointer transition-colors py-1 pr-2 pl-2 opacity-60 hover:opacity-100 ${
                      isNeutral
                        ? "hover:bg-gray-200 dark:hover:bg-white/10 text-gray-700 dark:text-gray-300"
                        : "hover:bg-white/10 text-white"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <MoreHorizontal size={14} />
                    </div>
                    <span className="truncate pl-1.5 font-medium">더 보기</span>
                  </Link>
                )}
              </SidebarSection>

              {/* 즐겨찾기 섹션 */}
              <SidebarSection
                title="즐겨찾기"
                icon={<Star size={14} />}
                isEmpty={favoriteDocs.length === 0}
                emptyMessage="즐겨찾기한 문서가 없습니다."
              >
                {favoriteDocs.map((doc: any) => (
                  <SidebarTreeItem key={`fav-${doc.id}`} doc={doc} level={0} section="favorite" />
                ))}
              </SidebarSection>

              {/* 팀 스페이스 섹션 */}
              <SidebarSection
                title={user?.role === "ADMIN" ? "전체 팀 스페이스" : "팀 스페이스"}
                icon={<Users size={14} />}
                dragOverId={dragOverId}
                onDragOver={(e) => handleDragOver(e, "TEAM_ROOT")}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, null, "TEAM")}
                onAddClick={user?.role === "USER" ? () => openCreateModal("TEAM", null, "팀 스페이스") : undefined}
                isEmpty={teamRootDocs.length === 0}
                emptyMessage="팀 문서가 없습니다."
              >
                {teamRootDocs.map((doc: any) => (
                  <SidebarTreeItem key={doc.id} doc={doc} level={0} section="team" />
                ))}
              </SidebarSection>

              {/* 개인 페이지 섹션 (USER만) */}
              {user?.role === "USER" && (
                <SidebarSection
                  title="개인 페이지"
                  icon={<FileText size={14} />}
                  dragOverId={dragOverId}
                  onDragOver={(e) => handleDragOver(e, "PERSONAL_ROOT")}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, null, "PERSONAL")}
                  onAddClick={() => openCreateModal("PERSONAL", null, "개인 페이지")}
                  isEmpty={personalRootDocs.length === 0}
                  emptyMessage="개인 문서가 없습니다."
                >
                  {personalRootDocs.map((doc: any) => (
                    <SidebarTreeItem key={doc.id} doc={doc} level={0} section="personal" />
                  ))}
                </SidebarSection>
              )}

              {/* 인계 페이지 섹션 (ADMIN만) — 퇴사자 개인 문서 보관 공간 */}
              {user?.role === "ADMIN" && (
                <SidebarSection
                  title="인계 페이지"
                  icon={<ArrowLeftRight size={14} />}
                  dragOverId={dragOverId}
                  onDragOver={(e) => handleDragOver(e, "PERSONAL_ROOT")}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, null, "PERSONAL")}
                  isEmpty={handoverRootDocs.length === 0}
                  emptyMessage="인계된 문서가 없습니다."
                >
                  {handoverRootDocs.map((doc: any) => (
                    <SidebarTreeItem key={doc.id} doc={doc} level={0} section="personal" />
                  ))}
                </SidebarSection>
              )}
            </>
          )}

          {/* 하단 유틸 링크 — 문서 리스트와 동일한 스타일 */}
          <div className="space-y-0.5">
            <Link
              href="/library"
              className={`flex items-center gap-2.5 px-2 py-1.5 rounded text-[13px] font-medium transition-colors ${isNeutral ? "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10" : "text-white/80 hover:bg-white/10"}`}
            >
              <Library size={14} />
              <span>라이브러리</span>
            </Link>
            <button
              onClick={() => setIsTrashOpen(true)}
              className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded text-[13px] font-medium transition-colors ${isNeutral ? "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10" : "text-white/80 hover:bg-white/10"}`}
            >
              <Trash2 size={14} />
              <span>휴지통</span>
            </button>
          </div>
        </div>

        {/* ── 모달 / 오버레이 ── */}
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          colorTheme={colorTheme}
          onColorChange={setColorTheme}
        />
        <ContextMenu
          contextMenu={contextMenu}
          onClose={() => setContextMenu((prev) => ({ ...prev, visible: false }))}
          onRename={handleMenuRename}
          onDelete={handleMenuDelete}
          onDuplicate={handleMenuDuplicate}
          onToggleFavorite={handleToggleFavorite}
          onRemoveFromRecent={handleRemoveFromRecent}
          onEndShare={handleEndShare}
          onCopyLink={handleCopyLink}
          onMove={handleOpenMove}
          onPermissionSettings={handleOpenPermissionModal}
          onOpenNewTab={handleOpenNewTab}
          onOpenSideView={handleOpenSideView}
        />
        <TrashModal isOpen={isTrashOpen} onClose={() => setIsTrashOpen(false)} />
        <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} onOpenSettings={() => setIsSettingsOpen(true)} />
      </div>

      {/* SidebarContext 밖에 렌더링되는 모달들 (z-index 충돌 방지) */}
      {permissionModal?.visible && (
        <DocPermissionModal
          docId={permissionModal.docId}
          docTitle={permissionModal.docTitle}
          primaryDept={user?.department || ""}
          currentUser={user}
          isGuestAllowed={permissionModal.isGuestAllowed}
          creatorName={permissionModal.creatorName}
          onClose={() => setPermissionModal(null)}
        />
      )}

      <CreatePageModal
        isOpen={createModal.visible}
        parentTitle={createModal.parentTitle}
        titleValue={createModal.title}
        onTitleChange={(val) => setCreateModal({ ...createModal, title: val })}
        onClose={() => setCreateModal({ ...createModal, visible: false })}
        onSubmit={submitCreateDocument}
        /* 템플릿 선택 */
        accessToken={user?.accessToken}
        templateId={createModal.templateId}
        onTemplateChange={(id) => setCreateModal({ ...createModal, templateId: id })}
      />

      <RenameModal
        visible={renameModal.visible}
        documentId={renameModal.docId}
        title={renameModal.title}
        x={renameModal.x}
        y={renameModal.y}
        icon={renameModal.icon}
        onTitleChange={(val) => setRenameModal((prev: any) => ({ ...prev, title: val }))}
        onClose={() => setRenameModal((prev: any) => ({ ...prev, visible: false }))}
        onSubmit={submitRename}
        onIconChange={(icon) => setRenameModal((prev: any) => ({ ...prev, icon }))}
      />

      <DeleteModal
        visible={deleteModal.visible}
        title={deleteModal.title || "새 페이지"}
        onClose={() => setDeleteModal({ visible: false, docId: "", title: "" })}
        onSubmit={submitDelete}
      />

      <MoveDocModal
        visible={moveModal.visible}
        docId={moveModal.docId}
        documents={documents}
        user={user}
        sourceWorkspaceType={moveModal.sourceWorkspaceType}
        x={moveModal.x}
        y={moveModal.y}
        onClose={() => setMoveModal((prev) => ({ ...prev, visible: false }))}
        onMove={handleConfirmMove}
      />

      {/* 드래그앤드롭으로 개인↔팀 이관 시 표시되는 확인 모달 */}
      <TransferConfirmModal
        isOpen={transferModal.isOpen}
        type={transferModal.type}
        payload={transferModal.payload}
        onClose={() => setTransferModal({ isOpen: false, type: null, payload: null })}
        onExecute={(action, payload: TransferPayload) =>
          executeTransfer(
            action,
            payload.draggedDocId,
            payload.targetParentId,
            payload.targetWorkspaceType,
            payload.draggedDoc,
          )
        }
      />
      <ToastContainer />
    </SidebarContext.Provider>
  );
}
